import { auth } from './firebaseConfig';
import { env } from '@/lib/env';
import axios, { type AxiosError, HttpStatusCode } from 'axios';

// Flag to prevent concurrent token refreshes
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (outcome: { error: unknown } | { token: string }) => {
  failedQueue.forEach(prom => {
    if ('error' in outcome) {
      prom.reject(outcome.error);
    } else {
      prom.resolve(outcome.token);
    }
  });
  failedQueue = [];
};

const getBaseURL = () => {
  const isLocalhost =
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  if (!isLocalhost) return 'https://api.h2owatercoolers.com/';

  // Local/e2e override only (apps/ui/AGENTS.md § Verification commands) — e.g. apps/api running
  // on a non-default port because :8080 is already taken. Unset in every other environment, so
  // the default below is what every normal local run still gets.
  const override = env.VITE_API_URL;
  if (typeof override !== 'string' || override === '') return 'http://localhost:8080/';
  return override.endsWith('/') ? override : `${override}/`;
};

const axiosInstance = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-type': 'application/json'
  }
});

axiosInstance.interceptors.request.use(
  async config => {
    const token = localStorage.getItem('token');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  error => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized - token expired or invalid
    if (
      error.response &&
      error.response.status === HttpStatusCode.Unauthorized &&
      auth.currentUser &&
      originalRequest &&
      !originalRequest.headers._retry
    ) {
      if (isRefreshing) {
        // Queue request while token is being refreshed
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest.headers._retry = true;
      isRefreshing = true;

      try {
        // Refresh the token
        const token = await auth.currentUser.getIdToken(true);
        localStorage.setItem('token', token);
        originalRequest.headers.Authorization = `Bearer ${token}`;
        processQueue({ token });
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue({ error: refreshError });
        // Clear token and redirect to login
        localStorage.removeItem('token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle server errors - log for debugging but show generic message
    if (error.response && error.response.status >= 500) {
      console.error('Server error:', error.response.status, error.response.data);
      // Don't expose server details to users
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
